import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart3, Package, ShoppingCart, Users, Settings, LogOut,
  Search, ChevronDown, ChevronUp, Edit2, Trash2, Save, X,
  Phone, MapPin, DollarSign, TrendingUp, Clock, Filter,
  Download, RefreshCw, Eye, CheckCircle, AlertCircle, Truck
} from 'lucide-react';
import './Admin.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_MAP = {
  pending: { label: 'Pendente', color: '#e6a817' },
  confirmed: { label: 'Confirmado', color: '#2563eb' },
  preparing: { label: 'Preparando', color: '#d97706' },
  delivered: { label: 'Entregue', color: '#16a34a' },
  cancelled: { label: 'Cancelado', color: '#dc2626' },
};

// ---- Login Screen ----
function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { username, password });
      sessionStorage.setItem('adminAuth', 'true');
      onLogin();
    } catch {
      setError('Usuário ou senha incorretos');
      setPassword('');
    }
  };

  return (
    <div className="adm-login-wrap" data-testid="admin-login-screen">
      <form className="adm-login-card" onSubmit={handleSubmit}>
        <div className="adm-login-logo">
          <Package size={40} />
        </div>
        <h1>Painel Admin</h1>
        <p className="adm-login-sub">Empadão da Ilha</p>
        <div className="adm-field">
          <label>Usuário</label>
          <input type="text" value={username} onChange={e => setUsername(e.target.value)} autoFocus required data-testid="admin-username-input" />
        </div>
        <div className="adm-field">
          <label>Senha</label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="admin-password-input" />
        </div>
        {error && <div className="adm-login-err" data-testid="admin-login-error"><AlertCircle size={14} /> {error}</div>}
        <button type="submit" className="adm-login-btn" data-testid="admin-login-button">Entrar</button>
      </form>
    </div>
  );
}

// ---- Sidebar ----
function Sidebar({ active, setActive, onLogout }) {
  const items = [
    { key: 'dashboard', icon: BarChart3, label: 'Dashboard' },
    { key: 'orders', icon: ShoppingCart, label: 'Pedidos' },
    { key: 'products', icon: Package, label: 'Produtos' },
    { key: 'customers', icon: Users, label: 'Clientes' },
    { key: 'insights', icon: TrendingUp, label: 'Insights' },
  ];
  return (
    <aside className="adm-sidebar" data-testid="admin-sidebar">
      <div className="adm-sidebar-brand">
        <Package size={24} />
        <span>Admin</span>
      </div>
      <nav className="adm-sidebar-nav">
        {items.map(i => (
          <button
            key={i.key}
            className={`adm-nav-item ${active === i.key ? 'active' : ''}`}
            onClick={() => setActive(i.key)}
            data-testid={`nav-${i.key}`}
          >
            <i.icon size={18} />
            <span>{i.label}</span>
          </button>
        ))}
      </nav>
      <button className="adm-nav-item adm-logout" onClick={onLogout} data-testid="admin-logout-button">
        <LogOut size={18} />
        <span>Sair</span>
      </button>
    </aside>
  );
}

// ---- Stat Card ----
function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="adm-stat" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="adm-stat-icon" style={{ background: color + '18', color }}>
        <Icon size={22} />
      </div>
      <div>
        <p className="adm-stat-label">{label}</p>
        <p className="adm-stat-value">{value}</p>
        {sub && <p className="adm-stat-sub">{sub}</p>}
      </div>
    </div>
  );
}

// ---- Dashboard ----
function DashboardView({ stats, period, setPeriod, insights, onExportExcel }) {
  if (!stats) return <div className="adm-empty">Carregando...</div>;

  return (
    <div data-testid="dashboard-view">
      <div className="adm-top-bar">
        <h2>Dashboard</h2>
        <div className="adm-top-actions">
          <div className="adm-period-btns">
            {['today','week','month','all'].map(p => (
              <button key={p} className={period === p ? 'active' : ''} onClick={() => setPeriod(p)} data-testid={`period-${p}`}>
                {p === 'today' ? 'Hoje' : p === 'week' ? '7 Dias' : p === 'month' ? '30 Dias' : 'Tudo'}
              </button>
            ))}
          </div>
          <button className="adm-btn-green" onClick={onExportExcel} data-testid="export-excel-btn">
            <Download size={16} /> Excel
          </button>
        </div>
      </div>

      <div className="adm-stats-grid">
        <StatCard icon={ShoppingCart} label="Pedidos" value={stats.totalOrders} color="#2563eb" />
        <StatCard icon={DollarSign} label="Faturamento" value={`R$ ${stats.totalRevenue.toFixed(2)}`} color="#16a34a" />
        <StatCard icon={TrendingUp} label="Ticket Médio" value={`R$ ${stats.avgTicket.toFixed(2)}`} color="#d97706" />
        <StatCard icon={Users} label="Clientes" value={stats.totalCustomers || 0} color="#7c3aed" />
      </div>

      <div className="adm-row-2">
        <div className="adm-card">
          <h3>Top Produtos</h3>
          {stats.topProducts?.length > 0 ? (
            <div className="adm-mini-table">
              {stats.topProducts.map((p) => (
                <div key={p.name} className="adm-mini-row">
                  <span className="adm-rank">#{stats.topProducts.indexOf(p) + 1}</span>
                  <span className="adm-name">{p.name}</span>
                  <span className="adm-qty">{p.quantity}x</span>
                </div>
              ))}
            </div>
          ) : <p className="adm-empty-text">Nenhum pedido ainda</p>}
        </div>
        <div className="adm-card">
          <h3>Pagamentos</h3>
          {Object.keys(stats.paymentMethods || {}).length > 0 ? (
            <div className="adm-mini-table">
              {Object.entries(stats.paymentMethods).map(([m, c]) => (
                <div key={m} className="adm-mini-row">
                  <span className="adm-name">{m}</span>
                  <span className="adm-qty">{c}</span>
                </div>
              ))}
            </div>
          ) : <p className="adm-empty-text">Nenhum dado</p>}
        </div>
      </div>
    </div>
  );
}

// ---- Orders ----
function OrdersView({ refresh }) {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await axios.get(`${API}/admin/orders?${params}`);
      setOrders(res.data.orders);
      setTotal(res.data.total);
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [search, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders, refresh]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/status`, { status: newStatus });
      fetchOrders();
    } catch (e) { alert('Erro ao atualizar status'); }
  };

  return (
    <div data-testid="orders-view">
      <div className="adm-top-bar">
        <h2>Pedidos ({total})</h2>
        <div className="adm-top-actions">
          <div className="adm-search">
            <Search size={16} />
            <input placeholder="Buscar por nome, telefone..." value={search} onChange={e => setSearch(e.target.value)} data-testid="orders-search" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="adm-select" data-testid="orders-status-filter">
            <option value="all">Todos</option>
            <option value="pending">Pendentes</option>
            <option value="confirmed">Confirmados</option>
            <option value="preparing">Preparando</option>
            <option value="delivered">Entregues</option>
            <option value="cancelled">Cancelados</option>
          </select>
        </div>
      </div>

      {loading ? <div className="adm-empty">Carregando...</div> : orders.length === 0 ? (
        <div className="adm-empty-state" data-testid="orders-empty">
          <ShoppingCart size={48} />
          <p>Nenhum pedido encontrado</p>
        </div>
      ) : (
        <div className="adm-orders-list">
          {orders.map(order => {
            const isExpanded = expandedId === order.orderId;
            const st = STATUS_MAP[order.orderStatus || order.status || 'pending'] || STATUS_MAP.pending;
            return (
              <div key={order.orderId} className="adm-order-card" data-testid={`order-${order.orderId}`}>
                <div className="adm-order-header" onClick={() => setExpandedId(isExpanded ? null : order.orderId)}>
                  <div className="adm-order-info">
                    <span className="adm-order-id">#{order.orderId?.slice(0, 8)}</span>
                    <span className="adm-order-name">{order.customerName || 'N/A'}</span>
                    <span className="adm-order-phone"><Phone size={12} /> {order.customerPhone || 'N/A'}</span>
                  </div>
                  <div className="adm-order-meta">
                    <span className="adm-order-total">R$ {(order.totalAmount || order.totalPrice || 0).toFixed(2)}</span>
                    <span className="adm-status-badge" style={{ background: st.color + '20', color: st.color }}>{st.label}</span>
                    <span className="adm-order-date">{order.createdAt ? new Date(order.createdAt).toLocaleDateString('pt-BR') : 'N/A'}</span>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="adm-order-detail">
                    <div className="adm-order-items">
                      <h4>Itens:</h4>
                      {order.items?.map((item, i) => (
                        <div key={i} className="adm-item-row">
                          <span>{item.productName || item.name}</span>
                          <span>{item.size && `(${item.size})`}</span>
                          <span>x{item.quantity}</span>
                          <span>R$ {(item.totalPrice || item.price * item.quantity || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="adm-order-details-grid">
                      {order.deliveryAddress && <p><MapPin size={14} /> {order.deliveryAddress}, {order.deliveryNeighborhood}</p>}
                      {order.paymentMethod && <p><DollarSign size={14} /> {order.paymentMethod}</p>}
                      {order.deliveryDateTime && <p><Clock size={14} /> {order.deliveryDateTime}</p>}
                      {order.deliveryType && <p><Truck size={14} /> {order.deliveryType}</p>}
                    </div>
                    <div className="adm-order-actions">
                      <span>Alterar status:</span>
                      {Object.entries(STATUS_MAP).map(([key, val]) => (
                        <button
                          key={key}
                          className={`adm-status-btn ${(order.orderStatus || order.status) === key ? 'current' : ''}`}
                          style={{ borderColor: val.color, color: val.color }}
                          onClick={() => updateStatus(order.orderId, key)}
                          data-testid={`status-btn-${key}`}
                        >
                          {val.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---- Products ----
function ProductsView({ refresh }) {
  const [products, setProducts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/products`);
      setProducts(res.data.products);
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts, refresh]);

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditData({ name: p.name, description: p.description, price: p.price, sizes: p.sizes ? JSON.parse(JSON.stringify(p.sizes)) : null });
  };

  const saveEdit = async (productId) => {
    try {
      const payload = {};
      if (editData.name) payload.name = editData.name;
      if (editData.description) payload.description = editData.description;
      if (editData.price !== undefined && editData.price !== null) payload.price = parseFloat(editData.price);
      if (editData.sizes) payload.sizes = editData.sizes;
      await axios.put(`${API}/admin/products/${productId}`, payload);
      setEditingId(null);
      fetchProducts();
    } catch (e) { alert('Erro ao salvar'); }
  };

  const deleteProduct = async (productId, name) => {
    if (!window.confirm(`Remover "${name}" do cardápio?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      fetchProducts();
    } catch (e) { alert('Erro ao deletar'); }
  };

  const updateSizePrice = (idx, newPrice) => {
    const sizes = [...editData.sizes];
    sizes[idx] = { ...sizes[idx], price: parseFloat(newPrice) || 0 };
    setEditData({ ...editData, sizes });
  };

  const grouped = {};
  products.forEach(p => {
    if (!grouped[p.category]) grouped[p.category] = [];
    grouped[p.category].push(p);
  });

  if (loading) return <div className="adm-empty">Carregando...</div>;

  return (
    <div data-testid="products-view">
      <div className="adm-top-bar">
        <h2>Produtos ({products.length})</h2>
        <button className="adm-btn-outline" onClick={fetchProducts}><RefreshCw size={16} /> Atualizar</button>
      </div>

      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className="adm-card adm-product-group">
          <h3 className="adm-cat-title">{cat} ({items.length})</h3>
          <div className="adm-product-table">
            <div className="adm-pt-header">
              <span>Produto</span>
              <span>Preço / Tamanhos</span>
              <span>Ações</span>
            </div>
            {items.map(p => {
              const isEditing = editingId === p.id;
              return (
                <div key={p.id} className="adm-pt-row" data-testid={`product-row-${p.id}`}>
                  <div className="adm-pt-name">
                    {p.image && <img src={p.image} alt={p.name} className="adm-pt-img" />}
                    <div>
                      {isEditing ? (
                        <>
                          <input className="adm-inline-input" value={editData.name} onChange={e => setEditData({...editData, name: e.target.value})} data-testid="edit-name-input" />
                          <input className="adm-inline-input small" value={editData.description} onChange={e => setEditData({...editData, description: e.target.value})} data-testid="edit-desc-input" />
                        </>
                      ) : (
                        <>
                          <strong>{p.name}</strong>
                          <small>{p.description}</small>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="adm-pt-price">
                    {isEditing ? (
                      p.sizes ? (
                        <div className="adm-size-edit">
                          {editData.sizes?.map((s, i) => (
                            <div key={i} className="adm-size-row">
                              <span>{s.size}</span>
                              <input type="number" step="0.01" value={s.price} onChange={e => updateSizePrice(i, e.target.value)} className="adm-price-input" data-testid={`edit-size-price-${i}`} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <input type="number" step="0.01" value={editData.price || ''} onChange={e => setEditData({...editData, price: e.target.value})} className="adm-price-input" data-testid="edit-price-input" />
                      )
                    ) : (
                      p.sizes ? (
                        <div className="adm-sizes-display">
                          {p.sizes.map((s, i) => (
                            <span key={i} className="adm-size-tag">{s.size}: R$ {s.price.toFixed(2)}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="adm-price-tag">R$ {p.price?.toFixed(2) || 'N/A'}</span>
                      )
                    )}
                  </div>
                  <div className="adm-pt-actions">
                    {isEditing ? (
                      <>
                        <button className="adm-icon-btn save" onClick={() => saveEdit(p.id)} data-testid="save-edit-btn"><Save size={16} /></button>
                        <button className="adm-icon-btn cancel" onClick={() => setEditingId(null)} data-testid="cancel-edit-btn"><X size={16} /></button>
                      </>
                    ) : (
                      <>
                        <button className="adm-icon-btn edit" onClick={() => startEdit(p)} data-testid={`edit-btn-${p.id}`}><Edit2 size={16} /></button>
                        <button className="adm-icon-btn delete" onClick={() => deleteProduct(p.id, p.name)} data-testid={`delete-btn-${p.id}`}><Trash2 size={16} /></button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Customers ----
function CustomersView({ refresh }) {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [expandedPhone, setExpandedPhone] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (search) params.set('search', search);
      const res = await axios.get(`${API}/admin/customers?${params}`);
      setCustomers(res.data.customers);
      setTotal(res.data.total);
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers, refresh]);

  const toggleExpand = async (phone) => {
    if (expandedPhone === phone) { setExpandedPhone(null); return; }
    setExpandedPhone(phone);
    try {
      const res = await axios.get(`${API}/admin/customers/${encodeURIComponent(phone)}/orders`);
      setCustomerOrders(res.data.orders);
    } catch { setCustomerOrders([]); }
  };

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/admin/export/customers`);
      const blob = new Blob([res.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clientes-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch { alert('Erro ao exportar'); }
  };

  return (
    <div data-testid="customers-view">
      <div className="adm-top-bar">
        <h2>Clientes ({total})</h2>
        <div className="adm-top-actions">
          <div className="adm-search">
            <Search size={16} />
            <input placeholder="Buscar por nome, telefone, bairro..." value={search} onChange={e => setSearch(e.target.value)} data-testid="customers-search" />
          </div>
          <button className="adm-btn-outline" onClick={exportCSV} data-testid="export-csv-btn"><Download size={16} /> CSV</button>
        </div>
      </div>

      {loading ? <div className="adm-empty">Carregando...</div> : customers.length === 0 ? (
        <div className="adm-empty-state" data-testid="customers-empty">
          <Users size={48} />
          <p>Nenhum cliente encontrado</p>
        </div>
      ) : (
        <div className="adm-customers-list">
          {customers.map((c) => (
            <div key={c.phone} className="adm-customer-card" data-testid={`customer-${c.phone}`}>
              <div className="adm-customer-header" onClick={() => toggleExpand(c.phone)}>
                <div className="adm-customer-info">
                  <span className="adm-customer-name">{c.name || 'Sem nome'}</span>
                  <span className="adm-customer-phone"><Phone size={12} /> {c.phone}</span>
                  {c.neighborhood && <span className="adm-customer-loc"><MapPin size={12} /> {c.neighborhood}</span>}
                </div>
                <div className="adm-customer-stats">
                  <span className="adm-cstat">{c.totalOrders || 0} pedidos</span>
                  <span className="adm-cstat">R$ {(c.totalSpent || 0).toFixed(2)}</span>
                  {expandedPhone === c.phone ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>
              {expandedPhone === c.phone && (
                <div className="adm-customer-detail">
                  <div className="adm-customer-full">
                    {c.address && <p><MapPin size={14} /> {c.address}</p>}
                    {c.favoritePaymentMethod && <p><DollarSign size={14} /> Pagamento preferido: {c.favoritePaymentMethod}</p>}
                    {c.firstOrderDate && <p><Clock size={14} /> Primeiro pedido: {new Date(c.firstOrderDate).toLocaleDateString('pt-BR')}</p>}
                  </div>
                  {customerOrders.length > 0 && (
                    <div className="adm-customer-orders">
                      <h4>Histórico de Pedidos</h4>
                      {customerOrders.slice(0, 5).map((o) => (
                        <div key={o.orderId || o.createdAt} className="adm-co-row">
                          <span>{new Date(o.createdAt).toLocaleDateString('pt-BR')}</span>
                          <span>{o.items?.length || 0} itens</span>
                          <span>R$ {(o.totalAmount || 0).toFixed(2)}</span>
                          <span className="adm-status-badge" style={{
                            background: (STATUS_MAP[o.orderStatus]?.color || '#999') + '20',
                            color: STATUS_MAP[o.orderStatus]?.color || '#999'
                          }}>{STATUS_MAP[o.orderStatus]?.label || 'Pendente'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Insights ----
function InsightsView({ insights }) {
  if (!insights) return <div className="adm-empty">Carregando...</div>;
  return (
    <div data-testid="insights-view">
      <div className="adm-top-bar"><h2>Insights</h2></div>
      <div className="adm-insights-grid">
        <div className="adm-card">
          <h3><MapPin size={18} /> Top Bairros</h3>
          {Object.keys(insights.topNeighborhoods || {}).length > 0 ? (
            <div className="adm-mini-table">
              {Object.entries(insights.topNeighborhoods).map(([n, c]) => (
                <div key={n} className="adm-mini-row"><span className="adm-name">{n}</span><span className="adm-qty">{c} clientes</span></div>
              ))}
            </div>
          ) : <p className="adm-empty-text">Nenhum dado</p>}
        </div>
        <div className="adm-card">
          <h3><DollarSign size={18} /> Formas de Pagamento</h3>
          {Object.keys(insights.paymentMethods || {}).length > 0 ? (
            <div className="adm-mini-table">
              {Object.entries(insights.paymentMethods).map(([m, c]) => (
                <div key={m} className="adm-mini-row"><span className="adm-name">{m}</span><span className="adm-qty">{c}</span></div>
              ))}
            </div>
          ) : <p className="adm-empty-text">Nenhum dado</p>}
        </div>
        <div className="adm-card">
          <h3><TrendingUp size={18} /> Conversão</h3>
          <p className="adm-big-number">{insights.conversionRate || 0}%</p>
          <p className="adm-sub-text">Carrinhos convertidos em pedidos</p>
        </div>
        <div className="adm-card">
          <h3><Users size={18} /> Clientes Recorrentes</h3>
          <p className="adm-big-number">{insights.returningCustomers || 0} / {insights.totalCustomers || 0}</p>
          <p className="adm-sub-text">
            {insights.totalCustomers > 0
              ? `${((insights.returningCustomers / insights.totalCustomers) * 100).toFixed(1)}% voltaram`
              : 'Aguardando dados'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ---- Main Admin ----
const Admin = () => {
  const [authenticated, setAuthenticated] = useState(sessionStorage.getItem('adminAuth') === 'true');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState(null);
  const [period, setPeriod] = useState('week');
  const [refreshKey, setRefreshKey] = useState(0);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, insightsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/stats?period=${period}`),
        axios.get(`${API}/admin/insights`),
      ]);
      setStats(statsRes.data);
      setInsights(insightsRes.data);
    } catch (_) { /* ignore */ }
  }, [period]);

  useEffect(() => {
    if (authenticated) loadData();
  }, [authenticated, loadData]);

  const exportExcel = async () => {
    try {
      const res = await axios.get(`${API}/reports/export-excel`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Empadao_Relatorio_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
    } catch { alert('Erro ao exportar Excel'); }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    setAuthenticated(false);
  };

  if (!authenticated) return <LoginScreen onLogin={() => setAuthenticated(true)} />;

  return (
    <div className="adm-layout" data-testid="admin-panel">
      <Sidebar active={activeTab} setActive={setActiveTab} onLogout={handleLogout} />
      <main className="adm-main">
        {activeTab === 'dashboard' && <DashboardView stats={stats} period={period} setPeriod={setPeriod} insights={insights} onExportExcel={exportExcel} />}
        {activeTab === 'orders' && <OrdersView refresh={refreshKey} />}
        {activeTab === 'products' && <ProductsView refresh={refreshKey} />}
        {activeTab === 'customers' && <CustomersView refresh={refreshKey} />}
        {activeTab === 'insights' && <InsightsView insights={insights} />}
      </main>
    </div>
  );
};

export default Admin;
